<script lang="ts">
  import LoadingSpinner from "@shared/svelte/Components/LoadingSpinner.svelte";
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import { onMount } from "svelte";
  import moment from "moment";
  import TableSortControl from "@shared/svelte/Components/TableSortControl.svelte";
  import * as _ from "lodash";
  import ChatWithSupportLink from "@shared/svelte/Components/ChatWithSupportLink.svelte";

  type Report = {
    item_name: string;
    product_line_id?: string;
    vendor_name: string;
    total_on_hand: number;
    total_consumption: number;
    total_par: number;
    order_quantity: number;
    order_container_type: string;
    item_size: string;
    item_upc: string;
  };

  let loading = true;
  let reportDates: { option: string; value: string }[];
  let reports: Report[];
  let selectedReportDate = null;
  let search = "";
  const companyName = (window as any).C.COMPANY_NAME;

  const fetchReports = async () => {
    try {
      loading = true;
      const res = await HttpPromise({
        method: "GET",
        url: "/api/retailer/sculpture_reports",
        data: {
          direction: sortFilters.direction,
          end_date: selectedReportDate,
          order: sortFilters.type,
          search,
        },
      });
      reportDates = res.report_dates.map((d: { end_date: string }) => {
        return {
          value: d.end_date,
          // @ts-ignore
          option: `Report date: ${moment(d.end_date).format("MMM D, YYYY")}`,
        };
      });
      if (!selectedReportDate && reportDates.length)
        selectedReportDate = reportDates[reportDates.length - 1].value;
      reports = res.reports;
      loading = false;
    } catch (error) {
      Flash.now({
        message: "There was an error while fetching sculpture reports. Please contact support",
        type: "error",
      });
    }
  };

  onMount(() => {
    (async () => {
      await fetchReports();
    })();
  });

  const runSearch = _.debounce(fetchReports, 250);
  const sortFilters = {
    type: "name",
    direction: "ASC",
    sortCallBack: ({ order, direction }) => {
      sortFilters.type = order;
      sortFilters.direction = direction;
      runSearch();
    },
  };

  let mobileSort: string = "name_asc";
  const mobileSortOptions = [
    { value: "name_asc", option: "Sort By: Name, Ascending" },
    { value: "name_desc", option: "Sort By: Name, Descending" },
    { value: "vendor_asc", option: "Sort By: Vendor, Ascending" },
    { value: "vendor_desc", option: "Sort By: Vendor, Descending" },
    { value: "on_hand_asc", option: "Sort By: On Hand, Ascending" },
    { value: "on_hand_desc", option: "Sort By: On Hand, Descending" },
    { value: "used_asc", option: "Sort By: Used, Ascending" },
    { value: "used_desc", option: "Sort By: Used, Descending" },
    { value: "par_asc", option: "Sort By: Par, Ascending" },
    { value: "par_desc", option: "Sort By: Par, Descending" },
    { value: "order_amount_asc", option: "Sort By: Order Amount, Ascending" },
    { value: "order_amount_desc", option: "Sort By: Order Amount, Descending" },
    { value: "order_uom_asc", option: "Sort By: Order (UOM), Ascending" },
    { value: "order_uom_desc", option: "Sort By: Order (UOM), Descending" },
    { value: "item_size_asc", option: "Sort By: Order (UOM), Ascending" },
    { value: "item_size_desc", option: "Sort By: Order (UOM), Descending" },
  ];
  const handleMobileSortSelect = e => {
    const optionWords = e.target.value.split("_");

    sortFilters.type =
      optionWords.length > 2 ? [optionWords[0], optionWords[1]].join("_") : optionWords[0];
    sortFilters.direction = optionWords[optionWords.length - 1].toUpperCase();

    runSearch();
  };

  const handleClearClick = e => {
    if (!loading && e?.target?.value === "") runSearch();
  };

</script>

<style>
  table thead {
    position: sticky;
    inset-block-start: 0px;
    z-index: 1;
  }

  .white-background {
    background-color: #ffffff;
  }

  .background-max-size {
    height: 100vh;
  }

  .padding-bottom-2 {
    padding-bottom: 2rem;
  }

</style>

<FizzStyles>
  <div class="white-background" class:background-max-size={loading}>
    <div class="fizz-container fizz-inset-squish-16 fizz-stack-24 fizz-space-between">
      <h4 class="fizz-heading-4">Sculpture Products</h4>
      <p class="fizz-text-small fizz-text-subdued">
        <strong>Report out of date?</strong> Contact your Sculpture rep to push the newest report to
        Provi
      </p>
    </div>
    {#if loading}
      <LoadingSpinner />
    {:else}
      <div class="fizz-container fizz-stack-24 fizz-inset-squish-16 fizz-flex-row-responsive">
        <select
          class="fizz-select"
          id="report-date"
          name="report-date"
          on:change={runSearch}
          bind:value={selectedReportDate}
        >
          <option disabled selected={false} value={null}> Select </option>
          {#each reportDates as { value, option }}
            <option selected={value === selectedReportDate} {value}>{option}</option>
          {/each}
        </select>
        <input
          type="search"
          id="product-search"
          name="product-search"
          placeholder="Search Products"
          class="fizz-input-search fizz-half-width"
          on:keyup={runSearch}
          bind:value={search}
          on:input={handleClearClick}
        />
        <select
          class="fizz-select fizz-hide-on-large"
          id="mobile-sort"
          name="mobile-sort"
          on:change={handleMobileSortSelect}
          bind:value={mobileSort}
        >
          {#each mobileSortOptions as { value, option }}
            <option selected={value === mobileSort} {value}>{option}</option>
          {/each}
        </select>
      </div>
      <div class="fizz-container fizz-stack-24 fizz-inset-squish-16">
        {#if reports.length === 0}
          <div class="fizz-text-center fizz-stack-24 fizz-text-subdued">
            <p class="fizz-stack-16">
              {#if search}
                No results found
              {:else}
                No reports found - talk to your Sculpture rep to have your reports pushed through
                here.
              {/if}
            </p>
          </div>
        {:else}
          <table class="fizz-table fizz-table-condensed">
            <thead>
              <tr>
                <th>
                  <div class="fizz-flex-row">
                    Name
                    <TableSortControl sortFilter={sortFilters} colName={"name"} />
                  </div>
                </th>
                <th>
                  <div class="fizz-flex-row">
                    Vendor
                    <TableSortControl sortFilter={sortFilters} colName={"vendor"} />
                  </div>
                </th>
                <th>
                  <div class="fizz-flex-row">
                    On Hand
                    <TableSortControl sortFilter={sortFilters} colName={"on_hand"} />
                  </div>
                </th>
                <th>
                  <div class="fizz-flex-row">
                    Used
                    <TableSortControl sortFilter={sortFilters} colName={"used"} />
                  </div>
                </th>
                <th>
                  <div class="fizz-flex-row">
                    Par
                    <TableSortControl sortFilter={sortFilters} colName={"par"} />
                  </div>
                </th>
                <th>
                  <div class="fizz-flex-row">
                    Order Amount
                    <TableSortControl sortFilter={sortFilters} colName={"order_amount"} />
                  </div>
                </th>
                <th>
                  <div class="fizz-flex-row">
                    Order (UOM)
                    <TableSortControl sortFilter={sortFilters} colName={"order_uom"} />
                  </div>
                </th>
                <th>
                  <div class="fizz-flex-row">
                    Item Size
                    <TableSortControl sortFilter={sortFilters} colName={"item_size"} />
                  </div>
                </th>
                <th>
                  <div class="fizz-flex-row">
                    Barcode
                    <TableSortControl sortFilter={sortFilters} colName={"barcode"} />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {#each reports as report}
                <tr>
                  <td data-label="Name">
                    {#if report.product_line_id}
                      <a href={`/products/${report.product_line_id}`} class="fizz-text-inherit">
                        {report.item_name}
                      </a>
                    {:else}
                      {report.item_name}
                    {/if}
                  </td>
                  <td data-label="Vendor">{report.vendor_name}</td>
                  <td data-label="On Hand">{report.total_on_hand}</td>
                  <td data-label="Used">{report.total_consumption}</td>
                  <td data-label="Par">{report.total_par}</td>
                  <td data-label="Order Amount">{report.order_quantity}</td>
                  <td data-label="Order (UOM)">{report.order_container_type}</td>
                  <td data-label="Item Size">{report.item_size}</td>
                  <td data-label="Bar Code">{report.item_upc}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        {/if}
      </div>
    {/if}
    <div class="fizz-text-center fizz-text-subdued padding-bottom-2">
      <p class="fizz-stack-16">
        Questions or feedback about the free {companyName} Report Beta?
      </p>
      <p>
        <ChatWithSupportLink message={"Chat with Support"} brandprimary={true} />
      </p>
    </div>
  </div>
</FizzStyles>
